import React from 'react';
import { fmtMoney, fmtDelta, fmtRate, fmtHours, sourceBadgeClass, asOf } from '../../utils/priceMath';
import { freshness, parseCalcLog, opsOwnersForStep, withShuttleRow, shuttleSentence } from '../../utils/partEntryCostMath';

/**
 * Presentational pieces for PartEntryPage's Pricing & Costing card — the
 * "show the math" tables. No fetches, no state beyond the <details> toggles;
 * every number comes in as a prop and every formula is printed beside it.
 */

const HOURS_SOURCE_LABEL = {
  historical: 'historical',
  estimate: 'estimate',
  mixed: 'mixed',
  theoretical_beam_on: 'theoretical · beam-on',
  theoretical_total_time: 'theoretical · total time',
  at_cost: 'at cost',
  vendor: 'vendor',
  none: 'no data',
};

const RATE_SOURCE_LABEL = {
  punch_weighted_90d: 'wages, 90-day punch-weighted',
  default: 'DEFAULT wage table (no punches)',
};

/** Small provenance badge. `kind` maps onto priceMath.sourceBadgeClass. */
export function SourceTag({ kind, children, title, className = '' }) {
  return (
    <span className={`${sourceBadgeClass(kind)} ${className}`.trim()} title={title} style={{ fontWeight: 500 }}>
      {children ?? HOURS_SOURCE_LABEL[kind] ?? kind}
    </span>
  );
}

/**
 * "Laser shuttle share" under Cost - Labor (wave 2, decision 4). The share is
 * ALREADY inside the labor figure the engine returned; this line only makes it
 * visible — and says when history already carries it.
 */
export function ShuttleLine({ shuttle, className = '' }) {
  const s = shuttleSentence(shuttle);
  const tag = shuttle && shuttle.available
    ? (shuttle.historyIncludes ? 'historical' : (shuttle.hoursSource === 'estimate' ? 'estimate' : null))
    : null;
  return (
    <div className={className} data-testid="laser-shuttle-share"
         title="First-run Laser parts carry a proportional table-swap (shuttle) share at the true rate, inside Cost - Labor; historical Laser hours already include the swaps, so none is added">
      Laser shuttle share: {s.text}{tag ? <> <SourceTag kind={tag} /></> : null}
    </div>
  );
}

/** Note column for a 'shuttle' log row. */
function shuttleNote(r) {
  return r.source === 'historical'
    ? 'first-run only — history includes it'
    : 'first-run proportional table-swap share · inside Cost - Labor';
}

/** "stored 09-01 12:54" / "stored (date n/a)" */
export function storedLabel(costUpdatedAt) {
  const s = asOf(costUpdatedAt);
  return s ? `stored ${s.replace(/^as of /, '')}` : 'stored (date n/a)';
}

function DeltaCell({ stored, live }) {
  const f = freshness(stored, live);
  if (!f.comparable) return <td className="text-muted">—</td>;
  if (!f.stale) return <td className="text-muted">±$0.00</td>;
  return (
    <td className={f.delta > 0 ? 'text-danger' : 'text-success'}>
      {fmtDelta(f.delta)} <SourceTag kind="stale">stale</SourceTag>
    </td>
  );
}

/**
 * Stored-vs-live reconciliation table. `rows` = [{key, item, basis, stored,
 * live, source (node), struck (bool), note (node)}]. `storedAsOf` heads the
 * Stored column so the reader knows what "stored" means in time.
 */
export function PartEntryCostMathTable({ rows, storedAsOf, hasStored }) {
  return (
    <div className="table-responsive">
      <table className="table table-sm table-bordered align-middle mb-1" style={{ fontSize: '0.8rem' }}>
        <thead className="table-light">
          <tr>
            <th>Item</th>
            <th>Basis</th>
            <th className="text-end">{hasStored ? `Stored (${storedAsOf})` : 'Stored'}</th>
            <th className="text-end">Live (this page)</th>
            <th>Δ live − stored</th>
            <th>Source / formula</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.key} className={r.emphasis ? 'fw-bold' : ''}>
              <td style={r.struck ? { textDecoration: 'line-through' } : undefined}>{r.item}</td>
              <td className="text-muted">{r.basis}</td>
              <td className="text-end">{hasStored ? fmtMoney(r.stored, { dash: true, precise: !!r.precise }) : <span className="text-muted">not saved yet</span>}</td>
              <td className="text-end" style={r.struck ? { textDecoration: 'line-through' } : undefined}>{fmtMoney(r.live, { dash: true, precise: !!r.precise })}</td>
              {hasStored ? <DeltaCell stored={r.stored} live={r.live} /> : <td className="text-muted">—</td>}
              <td>{r.source}{r.note ? <div className="text-muted">{r.note}</div> : null}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * The calculation log as a table (F08) instead of window.alert.
 * `log` is the plain-text log from calculate_cost / calculate-dynamic-cost.
 * Steps found in `opsOwnedIndex` are struck and annotated (F07).
 * `shuttle` (describeShuttle of the engine response) guarantees a "Laser shuttle
 * share" row even when the log predates the line (wave 2, decision 4).
 */
export function PartEntryCalcLogTable({ log, engine, engineAt, opsOwnedIndex, shuttle }) {
  const parsed = parseCalcLog(log);
  const engineLabel = engine === 'dynamic'
    ? 'estimate engine (POST /parts/calculate-dynamic-cost — typed minutes, unsaved)'
    : (engine === 'calculate_cost' ? 'history-aware engine (GET /parts/{id}/calculate_cost)' : (parsed.engine === 'dynamic' ? 'estimate engine' : 'engine n/a'));
  const rows = withShuttleRow(parsed.rows, shuttle);
  if (!log) {
    return <div className="small text-muted">No calculation log yet — save the part or change a routing step to run the cost engine.</div>;
  }
  return (
    <div>
      <div className="small text-muted mb-1">
        {engineLabel}{engineAt ? ` · ${asOf(engineAt)}` : ''}
        {parsed.hoursSource ? <> · hours: <SourceTag kind={parsed.hoursSource} /></> : null}
        {parsed.summary.labor !== null ? <> · engine totals: labor {fmtMoney(parsed.summary.labor)}, gas {fmtMoney(parsed.summary.gas)}</> : null}
      </div>
      <div className="table-responsive">
        <table className="table table-sm table-bordered align-middle mb-1" style={{ fontSize: '0.8rem' }}>
          <thead className="table-light">
            <tr>
              <th>Step</th>
              <th>Source</th>
              <th className="text-end">Hours</th>
              <th className="text-end">$/hr (source)</th>
              <th className="text-end">$</th>
              <th>Note</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan="6" className="text-muted">The log had no parseable lines — raw text is below.</td></tr>
            )}
            {rows.map((r, i) => {
              const owners = r.step ? opsOwnersForStep(opsOwnedIndex, r.step) : [];
              const struck = owners.length > 0 && (r.kind === 'step' || r.kind === 'skipped' || r.kind === 'outsourced');
              const strike = struck ? { textDecoration: 'line-through' } : undefined;
              const rateSrcLabel = r.rateSource ? RATE_SOURCE_LABEL[r.rateSource] : (r.rateNote || null);
              return (
                <tr key={`${r.kind}-${r.step || 'x'}-${i}`} className={r.kind === 'other' ? 'text-muted' : ''}>
                  <td style={strike}>{r.kind === 'shuttle' ? <span className="ms-2">↳ {r.step}</span> : (r.step || <em>{r.raw}</em>)}</td>
                  <td>
                    {r.source ? <SourceTag kind={r.source === 'at_cost' ? 'default' : (r.source.startsWith('theoretical') ? 'estimate' : r.source)}>{HOURS_SOURCE_LABEL[r.source] || r.source}</SourceTag> : null}
                    {r.sourceTag && (!r.source || r.kind === 'consumables') ? <span className="text-muted ms-1">{r.sourceTag}</span> : null}
                  </td>
                  <td className="text-end">{r.hours !== null && r.hours !== undefined ? fmtHours(r.hours) : '—'}</td>
                  <td className="text-end">
                    {r.rate !== null && r.rate !== undefined ? fmtRate(r.rate) : '—'}
                    {rateSrcLabel ? (
                      <div>
                        <SourceTag kind={r.rateSource === 'default' ? 'default' : 'historical'} className={r.rateSource ? '' : 'bg-light text-dark border'}>{rateSrcLabel}</SourceTag>
                      </div>
                    ) : (r.kind === 'step' ? <div className="text-muted">wage-weighted rate (source not reported)</div> : null)}
                  </td>
                  <td className="text-end" style={strike}>
                    {r.cost !== null && r.cost !== undefined ? fmtMoney(r.cost, { precise: r.kind === 'gas' || r.kind === 'consumables' }) : '—'}
                  </td>
                  <td>
                    {r.kind === 'shuttle'
                      ? <span className="text-muted">{shuttleNote(r)}{r.note && r.note !== shuttleNote(r) ? ` · ${r.note}` : ''}</span>
                      : (r.note ? <span className="text-muted">{r.note}</span> : null)}
                    {struck ? (
                      <div>
                        <SourceTag kind="assembly_row">priced on {owners.map(o => `#${o.assembly_id} ${o.assembly_name}`).join(', ')} operations line</SourceTag>
                      </div>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <details className="small">
        <summary className="text-muted" style={{ cursor: 'pointer' }}>Raw engine log</summary>
        <pre className="bg-white border rounded p-2 mt-1 mb-0" style={{ whiteSpace: 'pre-wrap', fontSize: '0.75rem' }}>{log}</pre>
      </details>
    </div>
  );
}
